import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState } from 'react';
import ButtonComponent from './ButtonComponent';
import '../styles/saveBtnWithPopup.scss';
import { DataContext } from '../player/DataContext';
import globalStore from '../store';
import InputWithBtn from './InputWithBtn';
var SaveBtnWithPopup = function (props) {
    var _a;
    var _b = globalStore.useStoreState(function (state) { return state.playerStore; }), displayMode = _b.displayMode, zoomLevel = _b.zoomLevel, playerIconZoom = _b.playerIconZoom, isLoggedIn = _b.isLoggedIn;
    var btnConfig = globalStore.useStoreState(function (state) { return state.jsonDataStore; }).btnConfig;
    var fetchGroups = globalStore.useStoreActions(function (state) { return state.groupsStore; }).fetchGroups;
    var btnData = props.btnData, item = props.item, btnType = props.btnType, direction = props.direction, isParentRelative = props.isParentRelative, clickHandler = props.clickHandler, useViewportPosition = props.useViewportPosition;
    var _c = useContext(DataContext), showSaveModal = _c.showSaveModal, setShowSaveModal = _c.setShowSaveModal;
    var modalRef = useRef(null);
    var btnRef = useRef(null);
    var portalRef = useRef(null);
    if (useViewportPosition && !portalRef.current && typeof document !== 'undefined') {
        portalRef.current = document.createElement('div');
        portalRef.current.className = 'saveBtnPortal';
    }
    useEffect(function () {
        var el = portalRef.current;
        if (!el)
            return;
        document.body.appendChild(el);
        return function () {
            var _a;
            (_a = el.parentNode) === null || _a === void 0 ? void 0 : _a.removeChild(el);
        };
    }, []);
    var _d = useState({ left: 0 }), popupPos = _d[0], setPopupPos = _d[1];
    useEffect(function () {
        fetchGroups();
    }, []);
    useEffect(function () {
        if (btnRef.current) {
            setPopupPos({
                left: -btnRef.current.offsetWidth / 2,
            });
        }
    }, [displayMode, zoomLevel, playerIconZoom]);
    var onClickHandler = function () {
        clickHandler(btnData[item].id, btnData[item].type, undefined);
    };
    // ===== CLOSE ON OUTSIDE CLICK =====
    useEffect(function () {
        function handleClickOutside(event) {
            var target = event.target;
            if (modalRef.current && modalRef.current.contains(target))
                return;
            if (portalRef.current && portalRef.current.contains(target))
                return;
            // Ignore clicks anywhere inside a nested Popup (content or overlay).
            // The Popup handles its own dismissal; those clicks must not collapse
            // the parent panel underneath it.
            if (target instanceof Element && target.closest('.popupOverlay'))
                return;
            onClickHandler();
        }
        if (showSaveModal)
            document.addEventListener('mousedown', handleClickOutside);
        return function () { return document.removeEventListener('mousedown', handleClickOutside); };
    }, [showSaveModal]);
    // ===== FOCUS MANAGEMENT + INERT BACKGROUND =====
    useEffect(function () {
        if (!showSaveModal)
            return;
        // Auto-focus first button inside the panel
        requestAnimationFrame(function () {
            var _a, _b;
            var panel = (_b = ((_a = portalRef.current) !== null && _a !== void 0 ? _a : modalRef.current)) === null || _b === void 0 ? void 0 : _b.querySelector('.iwb-panel');
            if (panel) {
                var firstBtn = panel.querySelector('button');
                if (firstBtn)
                    firstBtn.focus();
            }
        });
        // Make rest of page inert
        var inertedElements = [];
        var container = modalRef.current;
        if (container) {
            Array.from(document.body.children).forEach(function (child) {
                var el = child;
                if (el.contains(container) || el === container)
                    return;
                if (el === portalRef.current)
                    return;
                if (!el.hasAttribute('inert')) {
                    el.setAttribute('inert', '');
                    el.setAttribute('aria-hidden', 'true');
                    inertedElements.push(el);
                }
            });
            var current = container.parentElement;
            while (current && current !== document.body) {
                Array.from(current.children).forEach(function (child) {
                    var el = child;
                    if (el.contains(container) || el === container)
                        return;
                    if (!el.hasAttribute('inert')) {
                        el.setAttribute('inert', '');
                        el.setAttribute('aria-hidden', 'true');
                        inertedElements.push(el);
                    }
                });
                current = current.parentElement;
            }
        }
        // Focus trap (Tab) + Escape to close
        var handleKeyDown = function (e) {
            var _a, _b;
            if (e.key === 'Escape') {
                // Don't close if a portaled context menu is open
                var contextMenu = document.querySelector('.iwb-contextMenu');
                if (contextMenu)
                    return;
                e.preventDefault();
                e.stopPropagation();
                onClickHandler();
                requestAnimationFrame(function () { var _a; return (_a = btnRef.current) === null || _a === void 0 ? void 0 : _a.focus(); });
                return;
            }
            if (e.key === 'Tab') {
                var panel = (_b = ((_a = portalRef.current) !== null && _a !== void 0 ? _a : modalRef.current)) === null || _b === void 0 ? void 0 : _b.querySelector('.iwb-panel');
                if (!panel)
                    return;
                var focusables = Array.from(panel.querySelectorAll('button:not([disabled]):not([tabindex="-1"]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter(function (el) { return !el.closest('[inert]') && !el.closest('[aria-hidden="true"]'); });
                if (focusables.length === 0)
                    return;
                var first = focusables[0];
                var last = focusables[focusables.length - 1];
                if (e.shiftKey) {
                    if (document.activeElement === first || !panel.contains(document.activeElement)) {
                        e.preventDefault();
                        last.focus();
                    }
                }
                else {
                    if (document.activeElement === last || !panel.contains(document.activeElement)) {
                        e.preventDefault();
                        first.focus();
                    }
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown, true);
        return function () {
            document.removeEventListener('keydown', handleKeyDown, true);
            inertedElements.forEach(function (el) {
                el.removeAttribute('inert');
                el.removeAttribute('aria-hidden');
            });
        };
    }, [showSaveModal]);
    return (_jsxs("div", { ref: modalRef, style: { position: 'relative', zIndex: showSaveModal ? 999 : undefined }, className: "saveBtnContainer", children: [_jsx(ButtonComponent, { imgSrc: isLoggedIn ? btnData[item].imgSrc : (_a = btnConfig.headerShare) === null || _a === void 0 ? void 0 : _a.imgSrc, reference: btnRef, defaultCSS: btnType['style'].defaultCSS, normalCSS: btnType['style'].normalCSS, ariaLabel: isLoggedIn ? btnData[item].ariaLabel : 'text.share', title: isLoggedIn ? btnData[item].title : 'text.share', showToolTip: true, direction: direction, isParentRelative: isParentRelative, useViewportPosition: useViewportPosition, isActive: showSaveModal, controlActiveFromOutside: true, clickActive: true, clickHandler: function (e) {
                    clickHandler(btnData[item].id, btnData[item].type, e);
                } }), showSaveModal && (
            // <div
            //   className={`iWBContainer ${displayMode === 'normal' ? `headerTopBeak atHeader` : 'footerBottomBeak atFooter'
            //     }`}
            //   style={
            //     {
            //       left: popupPos.left,
            //       // maxHeight: `calc(100% - ${2 * popupPos.top}px)`,
            //       // top: `${sizes.headerHeight / 16}rem`,
            //       maxHeight: '553px',
            //       width: '425px',
            //       '--beak-height': beakHeight + 'px',
            //       '--beak-pos': `calc(var(--zoomed-icon-size) / 2)`,
            //     } as CSSProperties
            //   }
            // >
            //   <ButtonComponent text="Save" normalCSS="saveBtn" defaultCSS="saveBtn" />
            //   <div className="labelRow">
            //     <div>{t('text.code')}</div>
            //     <div>{t('text.label')}</div>
            //   </div>
            //   <div className="container">
            //     {data.map((dta, idx) => {
            //       return (
            //         <div className="row" key={idx}>
            //           <div className="code-box">{dta.code}</div>
            //           <div style={{ width: '50%', display: 'flex' }}>
            //             {!dta.showLabel ? (
            //               <ButtonComponent
            //                 text="Save"
            //                 normalCSS="saveBtn"
            //                 defaultCSS="saveBtn"
            //                 clickHandler={(e: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>) => {
            //                   onAddLabelClick(idx);
            //                 }}
            //               />
            //             ) : (
            //               <div style={{ display: 'flex', gap: '0.5px' }}>
            //                 <div className="label-box">
            //                   <input
            //                     className="input"
            //                     onChange={e => onInputChange(e, idx)}
            //                     value={dta.label}
            //                     placeholder="Type Here"
            //                   />
            //                   <ButtonComponent
            //                     normalCSS="checkBtn"
            //                     defaultCSS="btn"
            //                     imgSrc={btnConfig.tick.imgSrc}
            //                     clickHandler={() =>
            //                       setData(prev => {
            //                         const newData = [...prev];
            //                         console.log(newData);
            //                         newData[idx].submitClick = true;
            //                         return newData;
            //                       })
            //                     }
            //                   />
            //                 </div>
            //                 {dta.submitClick && (
            //                   <ButtonComponent
            //                     normalCSS="deleteBtn"
            //                     defaultCSS="deleteBtn"
            //                     imgSrc={btnConfig.delete.imgSrc}
            //                     clickHandler={() => onDeleteClick(idx)}
            //                   />
            //                 )}
            //               </div>
            //             )}
            //           </div>
            //         </div>
            //       );
            //     })}
            //   </div>
            // </div>
            _jsx(InputWithBtn, { style: (function () {
                    var _a;
                    if (!useViewportPosition) {
                        return displayMode === 'normal'
                            ? { left: -60, top: 40 }
                            : { right: 0, bottom: 40 };
                    }
                    var rect = (_a = btnRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                    if (!rect)
                        return {};
                    return displayMode === 'normal'
                        ? { position: 'fixed', left: rect.left - 60, top: rect.bottom }
                        : { position: 'fixed', left: rect.right, bottom: 40 };
                })(), arrowPosition: displayMode === 'normal' ? 'top' : 'bottom' }))] }));
};
export default SaveBtnWithPopup;
{
    /* <div className="saveBtnContainer">
  <ButtonComponent
    key={btnData[item].id}
    reference={btnRef}
    imgSrc={btnData[item].imgSrc}
    defaultCSS={btnType['style'].defaultCSS}
    normalCSS={btnType['style'].normalCSS}
    ariaLabel={btnData[item].ariaLabel}
    // clickActive={tool?.activeState}
    title={btnData[item].title}
    showToolTip
    isParentRelative
    isWhiteBoard={false}
    direction="bottom"
    clickHandler={() => {
      setShowPopup(!showPopup);
    }}
  />
  {showPopup && (
    <div
      className="iWBContainer"
      style={{
        top: popupPos.top,
        left: popupPos.left,
        maxHeight: `calc(100% - ${2 * popupPos.top}px)`,
      }}
    >
      <div className="triangle"></div>
      <ButtonComponent text={t('text.save')} normalCSS="saveBtn" defaultCSS="saveBtn" />
      <div className="labelRow">
        <div>{t('text.code')}</div>
        <div>{t('text.label')}</div>
      </div>
      <div className="container">
        {data.map((dta, idx) => {
          return (
            <div className="row" key={idx}>
              <div className="code-box">{dta.code}</div>
              <div style={{ width: '50%', display: 'flex' }}>
                {!dta.showLabel ? (
                  <ButtonComponent
                    text={t('text.save')}
                    normalCSS="saveBtn"
                    defaultCSS="saveBtn"
                    clickHandler={() => {
                      onAddLabelClick(idx);
                    }}
                  />
                ) : (
                  <div className="label-box">
                    <input
                      className="input"
                      onChange={e => onInputChange(e, idx)}
                      value={dta.label}
                      placeholder="Type Here"
                    />
                    <ButtonComponent
                      normalCSS="checkBtn"
                      defaultCSS="btn"
                      imgSrc={imgSrc.tick}
                      clickHandler={() =>
                        setData(prev => {
                          const newData = [...prev];
                          console.log(newData);
                          newData[idx].submitClick = true;
                          return newData;
                        })
                      }
                    />
                  </div>
                )}
              </div>
              {dta.submitClick && (
                <ButtonComponent
                  normalCSS="deleteBtn"
                  defaultCSS="deleteBtn"
                  imgSrc={imgSrc.delete}
                  clickHandler={() => onDeleteClick(idx)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
    // <InputWithBtn
    //   style={{
    //     top: popupPos.top,
    //     left: popupPos.left,
    //     maxHeight: `calc(100% - ${2 * popupPos.top}px)`,
    //   }}
    // />
  )}
  </div> */
}
